import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Box,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as PreviewIcon,
  Autorenew as RegenerateIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { useAddItemMutation, useUpdateItemMutation } from '../../../hooks/base-hooks';
import { CacheKey } from '../../../types/cache-keys';
import AdminConfirmDialog from '../Common/AdminConfirmDialog';
import { fetchStream, putItem } from '../../../support/queries';
import { VersionScope } from '../../../types/snackableCourse';

type Props = {
  open: boolean;
  mode: 'create' | 'edit';
  resource?: any;
  onClose: () => void;
  onSaved: (savedResource?: any) => void;
  readOnly?: boolean;
  versionScope?: VersionScope | null;
};

type ResourceForm = {
  name: string;
  type: string;
  description: string;
  provider: string;
  url: string;
  external: boolean;
  product_id?: number | null;
  product_version_id?: number | null;
  product_license_id?: number | null;
  doc_version?: string | null;
  deployment_scope?: string | null;
  generation_scope?: string | null;
};

type SseEventName = 'status' | 'token' | 'final' | 'error' | 'done' | 'message' | 'ping';

const STREAM_FLUSH_MS = 50;

const emptyForm: ResourceForm = {
  name: '',
  type: '',
  description: '',
  provider: '',
  url: '',
  external: false,
  product_id: null,
  product_version_id: null,
  product_license_id: null,
  doc_version: '',
  deployment_scope: '',
  generation_scope: '',
};

const cleanVersionScope = (versionScope?: VersionScope | null): Partial<ResourceForm> => {
  if (!versionScope) return {};

  return {
    product_id: versionScope.product_id ?? null,
    product_version_id: versionScope.product_version_id ?? null,
    product_license_id: versionScope.product_license_id ?? null,
    doc_version: versionScope.doc_version ?? '',
    deployment_scope: versionScope.deployment_scope ?? '',
    generation_scope: versionScope.generation_scope ?? '',
  };
};

const hasMeaningfulVersionScope = (versionScope?: VersionScope | null) => {
  if (!versionScope) return false;

  return Boolean(
    versionScope.product_id ||
    versionScope.product_version_id ||
    versionScope.product_license_id ||
    versionScope.doc_version ||
    versionScope.deployment_scope ||
    versionScope.generation_scope
  );
};

export default function ResourceEditorDialog({
                                               open,
                                               mode,
                                               resource,
                                               onClose,
                                               onSaved,
                                               readOnly = false,
                                               versionScope = null,
                                             }: Props) {
  const isEdit = mode === 'edit' && !!resource;

  const [form, setForm] = useState<ResourceForm>(emptyForm);
  const [initialForm, setInitialForm] = useState<ResourceForm>(emptyForm);
  const [viewMode, setViewMode] = useState<'edit' | 'preview'>('edit');
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regenerateContext, setRegenerateContext] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);
  const [streamStatus, setStreamStatus] = useState<string | null>(null);
  const [streamedDescription, setStreamedDescription] = useState('');

  const abortControllerRef = useRef<AbortController | null>(null);
  const pendingTextRef = useRef('');
  const flushTimerRef = useRef<number | null>(null);

  const resolvedVersionScope = useMemo(() => {
    return cleanVersionScope(versionScope);
  }, [versionScope]);

  const versionScoped = hasMeaningfulVersionScope(versionScope);

  useEffect(() => {
    if (!open) return;

    if (isEdit && resource) {
      const nextForm: ResourceForm = {
        name: resource.name || '',
        type: resource.type || '',
        description: resource.description || '',
        provider: resource.provider || '',
        url: resource.url || '',
        external: !!resource.external,
        product_id: resource.product_id ?? resource.product?.id ?? null,
        product_version_id: resource.product_version_id ?? resource.product_version?.id ?? null,
        product_license_id: resource.product_license_id ?? resource.product_license?.id ?? null,
        doc_version: resource.doc_version || '',
        deployment_scope: resource.deployment_scope || '',
        generation_scope: resource.generation_scope || '',
      };

      setForm(nextForm);
      setInitialForm(nextForm);
      setViewMode('preview');
    } else {
      const scopedForm = {
        ...emptyForm,
        ...resolvedVersionScope,
      };

      setForm(scopedForm);
      setInitialForm(scopedForm);
      setViewMode(readOnly ? 'preview' : 'edit');
    }

    setShowRegenerateModal(false);
    setRegenerateContext('');
    setRegenerateError(null);
    setIsRegenerating(false);
    setStreamStatus(null);
    setStreamedDescription('');
  }, [resource, open, isEdit, mode, readOnly, resolvedVersionScope]);

  useEffect(() => {
    if (open && readOnly) setViewMode('preview');
  }, [open, readOnly]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      resetStreamBuffers();
    };
  }, []);

  const addResource = useAddItemMutation('db/resource', () => [CacheKey.Resource]);
  const updateResource = useUpdateItemMutation('db/resource', () => [CacheKey.Resource, String(resource?.id)]);

  const saving = addResource.isPending || updateResource.isPending;
  const disabledAll = saving || readOnly || isRegenerating;

  const hasUnsavedChanges = useMemo(() => {
    return (
      form.name !== initialForm.name ||
      form.type !== initialForm.type ||
      form.description !== initialForm.description ||
      form.provider !== initialForm.provider ||
      form.url !== initialForm.url ||
      form.external !== initialForm.external ||
      form.product_id !== initialForm.product_id ||
      form.product_version_id !== initialForm.product_version_id ||
      form.product_license_id !== initialForm.product_license_id ||
      form.doc_version !== initialForm.doc_version ||
      form.deployment_scope !== initialForm.deployment_scope ||
      form.generation_scope !== initialForm.generation_scope
    );
  }, [form, initialForm]);

  const flushPendingText = () => {
    if (!pendingTextRef.current) return;
    const chunk = pendingTextRef.current;
    pendingTextRef.current = '';
    setStreamedDescription((prev) => prev + chunk);
  };

  const scheduleFlush = () => {
    if (flushTimerRef.current !== null) return;

    flushTimerRef.current = window.setTimeout(() => {
      flushTimerRef.current = null;
      flushPendingText();
    }, STREAM_FLUSH_MS);
  };

  const resetStreamBuffers = () => {
    pendingTextRef.current = '';
    if (flushTimerRef.current !== null) {
      window.clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
  };

  const parseSseFrames = (buffer: string) => {
    const frames: string[] = [];
    let remaining = buffer;
    let idx = remaining.indexOf('\n\n');

    while (idx >= 0) {
      frames.push(remaining.slice(0, idx));
      remaining = remaining.slice(idx + 2);
      idx = remaining.indexOf('\n\n');
    }

    return { frames, remaining };
  };

  const parseSseEvent = (raw: string): { eventName: SseEventName; data: any } | null => {
    let eventName: SseEventName = 'message';
    const dataLines: string[] = [];

    for (const line of raw.split('\n')) {
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim() as SseEventName;
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trim());
      }
    }

    if (!dataLines.length) return null;

    const dataStr = dataLines.join('\n');
    try {
      return { eventName, data: JSON.parse(dataStr) };
    } catch {
      return null;
    }
  };

  const streamRegenerateDescription = async (): Promise<string> => {
    const question = (regenerateContext || '').trim();

    if (!question) {
      throw new Error('Additional context is required to regenerate content.');
    }

    setRegenerateError(null);
    setIsRegenerating(true);
    setStreamStatus('Starting...');
    setStreamedDescription('');
    resetStreamBuffers();

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const versionContextBlock = [
      form.product_id ? `Product ID: ${form.product_id}` : '',
      form.product_version_id ? `Product Version ID: ${form.product_version_id}` : '',
      form.product_license_id ? `Product License ID: ${form.product_license_id}` : '',
      form.doc_version ? `Document Version: ${form.doc_version}` : '',
      form.deployment_scope ? `Deployment Scope: ${form.deployment_scope}` : '',
      form.generation_scope ? `Generation Scope: ${form.generation_scope}` : '',
    ].filter(Boolean).join('\n');

    const fullContext = [
      'Existing resource data',
      `Name: ${form.name || ''}`,
      `Type: ${form.type || ''}`,
      `Provider: ${form.provider || ''}`,
      `URL: ${form.url || ''}`,
      `External: ${form.external ? 'Yes' : 'No'}`,
      versionContextBlock ? `Version scope:\n${versionContextBlock}` : '',
      'Current description:',
      form.description || '',
      '',
      'Instruction: Rewrite this resource description in clean markdown. Correct inaccuracies, preserve useful technical detail, remove duplicated headings, and incorporate the user correction request. Preserve the version/deployment scope and do not generalize across product versions.',
    ]
      .filter(Boolean)
      .join('\n');

    const resp = await fetchStream(
      'ai/chat/stream',
      {
        question,
        context: fullContext,
        related_type: 'resource',
        related_id: String(resource?.id || 'new-resource'),
        product: form.name || 'resource',
        session_id: `resource-regenerate-${resource?.id || 'new'}-${Date.now()}`,
        use_drift: true,
        metadata: {
          product_id: form.product_id,
          product_version_id: form.product_version_id,
          product_license_id: form.product_license_id,
          doc_version: form.doc_version,
          deployment_scope: form.deployment_scope,
          generation_scope: form.generation_scope,
        },
      },
      {
        signal: controller.signal,
      }
    );

    if (!resp.ok) {
      throw new Error(`Streaming request failed (${resp.status})`);
    }

    if (!resp.body) {
      throw new Error('Streaming response has no body');
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let finalText = '';
    let streamDone = false;

    const handleEvent = (eventName: SseEventName, data: any) => {
      switch (eventName) {
        case 'status':
          setStreamStatus(data?.message || 'Generating...');
          return;

        case 'ping':
          return;

        case 'token': {
          const t = data?.text || '';
          setStreamStatus(null);

          if (t) {
            finalText += t;
            pendingTextRef.current += t;
            scheduleFlush();
          }
          return;
        }

        case 'final':
          flushPendingText();
          setStreamStatus(null);

          if (typeof data?.response === 'string') {
            finalText = data.response;
            setStreamedDescription(data.response);
          }
          return;

        case 'error':
          throw new Error(data?.message || 'Generation failed');

        case 'done':
          streamDone = true;
          flushPendingText();
          setStreamStatus(null);
          return;

        default:
          return;
      }
    };

    try {
      while (!streamDone) {
        const r = await reader.read();
        if (r.done) break;

        buffer += decoder.decode(r.value, { stream: true });

        const { frames, remaining } = parseSseFrames(buffer);
        buffer = remaining;

        for (const f of frames) {
          const evt = parseSseEvent(f);
          if (!evt) continue;
          handleEvent(evt.eventName, evt.data);
        }
      }

      if (buffer.trim()) {
        const evt = parseSseEvent(buffer.trim());
        if (evt) handleEvent(evt.eventName, evt.data);
      }

      flushPendingText();
      return finalText;
    } finally {
      try {
        reader.releaseLock();
      } catch {
        // no-op
      }

      resetStreamBuffers();
      flushPendingText();
      setIsRegenerating(false);
      setStreamStatus(null);
      abortControllerRef.current = null;
    }
  };

  const handleGeneratePreview = async () => {
    try {
      await streamRegenerateDescription();
    } catch (error: any) {
      if (error?.name !== 'AbortError') {
        console.error('Failed to regenerate resource content:', error);
        setRegenerateError(error?.message || 'Failed to regenerate content.');
      }
      setIsRegenerating(false);
      setStreamStatus(null);
      resetStreamBuffers();
    }
  };

  const buildPayload = (descriptionOverride?: string) => ({
    name: form.name,
    type: form.type || null,
    description: descriptionOverride ?? form.description,
    provider: form.provider || null,
    url: form.url || null,
    external: !!form.external,
    product_id: form.product_id || null,
    product_version_id: form.product_version_id || null,
    product_license_id: form.product_license_id || null,
    doc_version: form.doc_version || null,
    deployment_scope: form.deployment_scope || null,
    generation_scope: form.generation_scope || null,
  });

  const handleUseRegeneratedContent = async () => {
    if (!streamedDescription) return;

    try {
      const nextPayload = buildPayload(streamedDescription);

      if (isEdit && resource?.id) {
        const updated = await putItem('db/resource', {
          ...nextPayload,
          id: resource.id,
        });

        setForm((prev) => ({
          ...prev,
          description: streamedDescription,
        }));
        setInitialForm((prev) => ({
          ...prev,
          description: streamedDescription,
        }));
        setViewMode('preview');
        setShowRegenerateModal(false);
        setRegenerateContext('');
        setStreamedDescription('');
        onSaved(updated);
        return;
      }

      setForm((prev) => ({
        ...prev,
        description: streamedDescription,
      }));
      setViewMode('preview');
      setShowRegenerateModal(false);
      setRegenerateContext('');
      setStreamedDescription('');
    } catch (error: any) {
      console.error('Failed to update resource after regeneration:', error);
      setRegenerateError(error?.message || 'Failed to save regenerated content.');
    }
  };

  const handleSave = async () => {
    if (readOnly) return;

    const payload = buildPayload();

    try {
      if (mode === 'create') {
        const created: any = await addResource.mutateAsync(payload);

        onClose();

        setTimeout(() => {
          onSaved(created);
        }, 0);
        return;
      }

      if (isEdit && resource?.id) {
        const updated: any = await updateResource.mutateAsync({
          ...payload,
          id: resource.id,
        });

        onClose();

        setTimeout(() => {
          onSaved(updated);
        }, 0);
      }
    } catch (error) {
      console.error('Failed to save resource:', error);
    }
  };

  const resetAndClose = () => {
    setForm(emptyForm);
    setInitialForm(emptyForm);
    setConfirmCloseOpen(false);
    setShowRegenerateModal(false);
    setRegenerateContext('');
    setRegenerateError(null);
    setStreamStatus(null);
    setStreamedDescription('');
    onClose();
  };

  const handleClose = () => {
    if (readOnly || !hasUnsavedChanges) {
      resetAndClose();
      return;
    }

    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    resetAndClose();
  };

  const title = readOnly
    ? mode === 'create'
      ? 'View Resource'
      : `View Resource: ${resource?.name || ''}`
    : mode === 'create'
      ? 'Create Resource'
      : `Edit Resource: ${resource?.name || ''}`;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>{title}</DialogTitle>

        <DialogContent>
          {versionScoped && (
            <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                Version-scoped resource
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                {form.doc_version && <Chip size="small" label={`Version: ${form.doc_version}`} />}
                {form.product_version_id && <Chip size="small" label={`Version ID: ${form.product_version_id}`} />}
                {form.deployment_scope && <Chip size="small" label={`Deployment: ${form.deployment_scope}`} />}
                {form.product_license_id && <Chip size="small" label={`License ID: ${form.product_license_id}`} />}
              </Box>
            </Alert>
          )}

          <TextField
            fullWidth
            margin="normal"
            label="Name"
            value={form.name ?? ''}
            onChange={(e) => !readOnly && setForm((p) => ({ ...p, name: e.target.value }))}
            required
            disabled={disabledAll}
          />

          <FormControl fullWidth margin="normal" disabled={disabledAll}>
            <InputLabel>Type</InputLabel>
            <Select
              value={form.type ?? ''}
              label="Type"
              onChange={(e) => !readOnly && setForm((p) => ({ ...p, type: e.target.value }))}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="what">What</MenuItem>
              <MenuItem value="why">Why It&apos;s Important</MenuItem>
              <MenuItem value="configure">How to Configure</MenuItem>
              <MenuItem value="validate">How to Validate</MenuItem>
              <MenuItem value="troubleshoot">How to Troubleshoot</MenuItem>
              <MenuItem value="differentiate">Competitive Differentiation</MenuItem>
              <MenuItem value="sell">How to Sell</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <Box mt={2} mb={1}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} gap={2}>
              <Typography variant="subtitle2" fontWeight={600}>
                Description *
              </Typography>

              <Box display="flex" alignItems="center" gap={1}>
                {!readOnly && (
                  <Button
                    variant="outlined"
                    startIcon={isRegenerating ? <CircularProgress size={16} /> : <RegenerateIcon />}
                    onClick={() => {
                      setRegenerateError(null);
                      setRegenerateContext('');
                      setStreamedDescription('');
                      setShowRegenerateModal(true);
                    }}
                    disabled={disabledAll}
                  >
                    {isRegenerating ? 'Regenerating...' : 'Regenerate'}
                  </Button>
                )}

                <ToggleButtonGroup
                  size="small"
                  value={viewMode}
                  exclusive
                  onChange={(_, newMode) => {
                    if (newMode === null) return;
                    if (readOnly && newMode === 'edit') return;
                    setViewMode(newMode);
                  }}
                >
                  <ToggleButton value="edit" disabled={readOnly}>
                    <EditIcon fontSize="small" sx={{ mr: 0.5 }} />
                    Edit
                  </ToggleButton>
                  <ToggleButton value="preview">
                    <PreviewIcon fontSize="small" sx={{ mr: 0.5 }} />
                    Preview
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>

            {viewMode === 'edit' ? (
              <TextField
                fullWidth
                value={form.description ?? ''}
                onChange={(e) => !readOnly && setForm((p) => ({ ...p, description: e.target.value }))}
                multiline
                minRows={12}
                maxRows={20}
                placeholder="Enter markdown content here..."
                required
                disabled={disabledAll}
                sx={{
                  '& .MuiInputBase-root': {
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                  },
                }}
              />
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  minHeight: 300,
                  maxHeight: 500,
                  overflow: 'auto',
                  bgcolor: 'grey.50',
                }}
              >
                {form.description ? (
                  <ReactMarkdown>{form.description}</ReactMarkdown>
                ) : (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    No content to preview
                  </Typography>
                )}
              </Paper>
            )}
          </Box>

          {regenerateError && (
            <Box mt={1}>
              <Typography color="error" variant="body2">
                {regenerateError}
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            margin="normal"
            label="Provider"
            value={form.provider ?? ''}
            onChange={(e) => !readOnly && setForm((p) => ({ ...p, provider: e.target.value }))}
            helperText="E.g., Cisco, Partner, Internal"
            disabled={disabledAll}
          />

          <TextField
            fullWidth
            margin="normal"
            label="URL"
            value={form.url ?? ''}
            onChange={(e) => !readOnly && setForm((p) => ({ ...p, url: e.target.value }))}
            helperText="Link to external resource or documentation"
            disabled={disabledAll}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={!!form.external}
                onChange={(e) => !readOnly && setForm((p) => ({ ...p, external: e.target.checked }))}
                disabled={disabledAll}
              />
            }
            label="External Resource"
          />

          <Box mt={2} display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
            <TextField
              label="Product ID"
              value={form.product_id ?? ''}
              disabled
              size="small"
            />
            <TextField
              label="Product Version ID"
              value={form.product_version_id ?? ''}
              disabled
              size="small"
            />
            <TextField
              label="Product License ID"
              value={form.product_license_id ?? ''}
              disabled
              size="small"
            />
            <TextField
              label="Doc Version"
              value={form.doc_version ?? ''}
              disabled
              size="small"
            />
            <TextField
              label="Deployment Scope"
              value={form.deployment_scope ?? ''}
              disabled
              size="small"
            />
            <TextField
              label="Generation Scope"
              value={form.generation_scope ?? ''}
              disabled
              size="small"
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={saving || isRegenerating}>
            {readOnly ? 'Close' : 'Cancel'}
          </Button>

          {!readOnly && (
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving || isRegenerating || !form.name || !form.description}
            >
              {saving ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={16} /> Saving
                </Box>
              ) : (
                'Save'
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={showRegenerateModal}
        onClose={() => {
          if (!isRegenerating) {
            setShowRegenerateModal(false);
            setRegenerateContext('');
            setStreamedDescription('');
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Regenerate Resource Content</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary">
              Add corrective context, generate an updated description, then apply it to this resource.
            </Typography>
            {versionScoped && (
              <Typography variant="caption" color="text.secondary">
                The regeneration prompt will preserve the selected version/deployment scope.
              </Typography>
            )}
          </Box>

          <TextField
            fullWidth
            multiline
            minRows={5}
            label="Additional Context"
            placeholder="Describe what is inaccurate and what should be corrected..."
            value={regenerateContext}
            onChange={(e) => setRegenerateContext(e.target.value)}
            disabled={isRegenerating}
          />

          <Box mt={2}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Regenerated Preview
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                minHeight: 220,
                maxHeight: 420,
                overflow: 'auto',
                bgcolor: 'grey.50',
              }}
            >
              {isRegenerating ? (
                streamedDescription ? (
                  <Box sx={{ whiteSpace: 'pre-wrap' }}>{streamedDescription}</Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {streamStatus || 'Generating...'}
                  </Typography>
                )
              ) : streamedDescription ? (
                <ReactMarkdown>{streamedDescription}</ReactMarkdown>
              ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  Generated content preview will appear here.
                </Typography>
              )}
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (!isRegenerating) {
                setShowRegenerateModal(false);
                setRegenerateContext('');
                setStreamedDescription('');
              }
            }}
            disabled={isRegenerating}
          >
            Cancel
          </Button>

          {!isRegenerating && !streamedDescription ? (
            <Button
              onClick={handleGeneratePreview}
              variant="contained"
              disabled={!regenerateContext.trim()}
            >
              Generate
            </Button>
          ) : isRegenerating ? (
            <Button variant="contained" disabled>
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} /> Generating
              </Box>
            </Button>
          ) : (
            <Button variant="contained" onClick={handleUseRegeneratedContent}>
              Use Regenerated Content
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <AdminConfirmDialog
        open={confirmCloseOpen}
        title="Discard Changes"
        message={
          <Box>
            <Typography variant="body1" gutterBottom>
              You have unsaved changes in this resource editor.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              If you continue, your changes will be lost.
            </Typography>
          </Box>
        }
        confirmLabel="Discard Changes"
        cancelLabel="Continue Editing"
        confirmColor="warning"
        loading={saving || isRegenerating}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
}